(function () {
  let activeTrigger;
  let activeOverlay;
  let cleanupBinding;

  function initNativeSearch() {
    const trigger = document.querySelector('[data-native-search-trigger]');
    const overlay = document.querySelector('[data-native-search-overlay]');
    if (trigger === activeTrigger && overlay === activeOverlay) return;
    cleanupBinding?.();
    if (!trigger || !overlay) return;
    activeTrigger = trigger;
    activeOverlay = overlay;

    const closeButton = overlay.querySelector('[data-native-search-close]');
    const input = overlay.querySelector('[data-native-search-input]');
    const results = overlay.querySelector('[data-native-search-results]');
    const related = overlay.querySelector('[data-native-search-related]');
    const status = overlay.querySelector('[data-native-search-status]');
    const main = document.querySelector('.main-wrap');
    const background = [...document.body.children].filter((element) => !element.contains(overlay) && !['SCRIPT', 'STYLE'].includes(element.tagName));
    let previousInert = new Map();
    let timer;
    let controller;
    let requestId = 0;

    function cancelSearch() {
      clearTimeout(timer);
      if (controller) controller.abort();
      controller = null;
      requestId += 1;
    }

    function resetSuggestions() {
      results.hidden = true;
      results.replaceChildren();
      related.hidden = false;
      status.textContent = '';
    }

    function openSearch(event) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (overlay.classList.contains('search--open')) return;
      previousInert = new Map(background.map((element) => [element, element.inert]));
      background.forEach((element) => { element.inert = true; });
      overlay.inert = false;
      overlay.setAttribute('aria-hidden', 'false');
      overlay.classList.add('search--open');
      main?.classList.add('main-wrap--move');
      document.body.classList.add('no-scroll');
      trigger.setAttribute('aria-expanded', 'true');
      input.focus();
      if (input.value.trim()) updateSuggestions();
    }

    function closeSearch(restoreFocus = true) {
      if (!overlay.classList.contains('search--open')) return;
      cancelSearch();
      overlay.classList.remove('search--open');
      overlay.inert = true;
      overlay.setAttribute('aria-hidden', 'true');
      previousInert.forEach((wasInert, element) => { element.inert = wasInert; });
      previousInert.clear();
      main?.classList.remove('main-wrap--move');
      document.body.classList.remove('no-scroll');
      trigger.setAttribute('aria-expanded', 'false');
      input.value = '';
      resetSuggestions();
      if (restoreFocus && trigger.isConnected) trigger.focus();
    }

    async function loadSuggestions(term, currentRequestId) {
      controller = new AbortController();
      const url = new URL(overlay.dataset.predictiveSearchUrl, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '4');
      url.searchParams.set('section_id', 'native-predictive-search');

      try {
        const response = await fetch(url.toString(), { signal: controller.signal });
        if (!response.ok) throw new Error('Predictive search request failed');
        const html = await response.text();
        if (currentRequestId !== requestId) return;
        const documentResult = new DOMParser().parseFromString(html, 'text/html');
        const content = documentResult.querySelector('#shopify-section-native-predictive-search [data-native-search-suggestions]');
        if (!content) throw new Error('Predictive search section missing');
        results.replaceChildren(content);
        const count = Number(content.dataset.resultCount) || 0;
        status.textContent = count ? `${count} product suggestions` : 'No product suggestions';
      } catch (error) {
        if (error.name === 'AbortError' || currentRequestId !== requestId) return;
        results.textContent = 'Suggestions are unavailable. Press Enter to search.';
        status.textContent = 'Suggestions are unavailable';
      }
    }

    function updateSuggestions() {
      cancelSearch();
      const term = input.value.trim();
      if (!term) {
        resetSuggestions();
        return;
      }
      related.hidden = true;
      results.hidden = false;
      results.textContent = 'Searching…';
      status.textContent = 'Searching';
      const currentRequestId = requestId;
      timer = setTimeout(() => loadSuggestions(term, currentRequestId), 200);
    }

    function handleCloseClick() { closeSearch(); }

    function handleOverlayKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeSearch();
      }
      if (event.key !== 'Tab') return;
      const focusable = [...overlay.querySelectorAll('a[href], button:not([disabled]), input:not([disabled])')]
        .filter((element) => element.getClientRects().length);
      if (!focusable.length) return;
      if (event.shiftKey && document.activeElement === focusable[0]) {
        event.preventDefault();
        focusable[focusable.length - 1].focus();
      } else if (!event.shiftKey && document.activeElement === focusable[focusable.length - 1]) {
        event.preventDefault();
        focusable[0].focus();
      }
    }

    trigger.addEventListener('click', openSearch);
    closeButton.addEventListener('click', handleCloseClick);
    input.addEventListener('input', updateSuggestions);
    overlay.addEventListener('keydown', handleOverlayKeydown);

    cleanupBinding = () => {
      closeSearch(false);
      cancelSearch();
      trigger.removeEventListener('click', openSearch);
      closeButton.removeEventListener('click', handleCloseClick);
      input.removeEventListener('input', updateSuggestions);
      overlay.removeEventListener('keydown', handleOverlayKeydown);
      activeTrigger = undefined;
      activeOverlay = undefined;
      cleanupBinding = undefined;
    };
  }

  function initSearchView() {
    const grid = document.querySelector('[data-native-search-grid]');
    const buttons = [...document.querySelectorAll('[data-native-search-view]')];
    if (!grid || !buttons.length || grid.dataset.viewBound) return;
    grid.dataset.viewBound = 'true';

    function setView(view) {
      grid.dataset.view = view;
      buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.nativeSearchView === view)));
    }

    try {
      if (sessionStorage.getItem('native-search-view') === 'list') setView('list');
    } catch (_) {
      // Storage can be unavailable in private browsing; the default grid still works.
    }

    buttons.forEach((button) => button.addEventListener('click', () => {
      const view = button.dataset.nativeSearchView;
      setView(view);
      try { sessionStorage.setItem('native-search-view', view); } catch (_) { /* Keep the current view. */ }
    }));
  }

  function initSearchFilters() {
    const form = document.querySelector('.native-search-filters');
    if (!form || form.dataset.filtersBound) return;
    form.dataset.filtersBound = 'true';

    form.addEventListener('change', (event) => {
      if (event.target.matches('input[type="checkbox"], select[name="sort_by"], .native-search-filter__price input')) {
        form.requestSubmit();
      }
    });
  }

  function init() {
    initNativeSearch();
    initSearchFilters();
    initSearchView();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', (event) => {
    if ((activeTrigger && event.target.contains(activeTrigger)) || (activeOverlay && event.target.contains(activeOverlay))) cleanupBinding?.();
  });
})();
